"use client"

import React, { useMemo, useState } from "react"
import { Modal, Form, Select, Button, Row, Col, message } from "antd"
import "./uploadImagesModal.css"
import CustomUpload from "../../Common/Uploadfile"

import {
  extractFiles,
  generateUniqueFileName,
  buildGalleryKey,
  uploadFileToS3,
  validateImageFile,
  getPublicUrl,
} from "../../../utils/s3Upload"

const UploadImagesModal = ({ visible, onCancel, onAdd }) => {
  const [form] = Form.useForm()
  const [uploading, setUploading] = useState(false)

  // Get language from localStorage
  const lang =
    typeof window !== "undefined"
      ? localStorage.getItem("language") || "en"
      : "en"
  const isArabic = lang === "ar"

  // Translations
  const t = {
    uploadImages: isArabic ? "رفع الصور" : "Upload Images",
    roomType: isArabic ? "جميع أنواع الغرف" : "Room Type",
    decorStyle: isArabic ? "ستايل الديكور" : "Décor Style",
    theme: isArabic ? "جميع الثيمات" : "Theme",
    uploadText: isArabic
      ? "تنسيقات PNG/JPEG (بحد أقصى 1 ميجابايت لكل صورة)"
      : "PNG/JPEG (max 1MB each)",
    uploadBtn: isArabic ? "رفع" : "Upload",
    uploading: isArabic ? "جاري الرفع..." : "Uploading...",
    requiredRoomType: isArabic
      ? "يرجى اختيار نوع الغرفة"
      : "Please select room type",
    requiredDecorStyle: isArabic
      ? "يرجى اختيار نمط الديكور"
      : "Please select décor style",
    requiredTheme: isArabic ? "يرجى اختيار السمة" : "Please select theme",
    mustSelectAll: isArabic
      ? "يرجى تحديد نوع الغرفة ونمط الديكور والسمة أولاً."
      : "Select Room Type, Décor Style, and Theme first.",
    pleaseUpload: isArabic
      ? "يرجى رفع صورة واحدة على الأقل."
      : "Please upload at least one image.",
    exceedsLimit: isArabic ? "يتجاوز حجم الصورة 1 ميجابايت" : "exceeds 1MB",
    successMsg: isArabic
      ? "تم رفع الصور بنجاح"
      : "Uploaded images successfully",
  }

  // Room types
  const roomTypeOptions = useMemo(
    () => [
      { value: "Bedroom", label: isArabic ? "غرفة النوم" : "Bedroom" },
      { value: "Library", label: isArabic ? "المكتبة" : "Library" },
      { value: "Gym", label: isArabic ? "الجيم" : "Gym" },
      { value: "Restaurant", label: isArabic ? "مطعم" : "Restaurants" },
      {
        value: "Living_Room",
        label: isArabic ? "الصالة" : "Living Room",
      },
      { value: "Dining", label: isArabic ? "غرفة الطعام" : "Dining" },
      { value: "Kitchen", label: isArabic ? "المطبخ" : "Kitchen" },
      { value: "Bathroom", label: isArabic ? "الحمام" : "Bathroom" },
      { value: "Garden", label: isArabic ? "الحديقة" : "Garden" },
      { value: "Kids_Room", label: isArabic ? "غرفة الأطفال" : "Kids Room" },
      { value: "Entrance", label: isArabic ? "المدخل" : "Entrance" },
      {
        value: "Gaming_Room",
        label: isArabic ? "غرفة الألعاب" : "Gaming Room",
      },
      { value: "Office", label: isArabic ? "المكتب" : "Office" },
    ],
    [isArabic]
  )

  // Décor styles
  const decoreStyleOptions = useMemo(
    () => [
      { value: "Islamic", label: isArabic ? "إسلامي" : "Islamic" },
      { value: "Minimalist", label: isArabic ? "مينيمال" : "Minimalist" },
      { value: "Tropical", label: isArabic ? "استوائي" : "Tropical" },
      { value: "Boho", label: isArabic ? "بوهيمي" : "Boho" },
      { value: "Modern", label: isArabic ? "مودرن" : "Modern" },
      { value: "Contemporary", label: isArabic ? "معاصر" : "Contemporary" },
      { value: "Classy", label: isArabic ? "كلاسيكي" : "Classy" },
      {
        value: "Modern_Classic",
        label: isArabic ? "مودرن كلاسيك" : "Modern Classic",
      },
      { value: "Shabby_Chic", label: isArabic ? "رث أنيق" : "Shabby Chic" },
      { value: "Eclectic", label: isArabic ? "انتقائي" : "Eclectic" },
      { value: "Industrial", label: isArabic ? "صناعي" : "Industrial" },
      {
        value: "Rustic_Modern",
        label: isArabic ? "ريفي حديث" : "Rustic Modern",
      },
      { value: "Zen", label: isArabic ? "زن ياباني" : "Zen" },
    ],
    [isArabic]
  )

  // Theme
  const themeOptions = useMemo(
    () => [
      { value: "Dark", label: isArabic ? "غامق" : "Dark" },
      { value: "Light", label: isArabic ? "فاتح" : "Light" },
      { value: "Moroccan", label: isArabic ? "مغربي" : "Moroccan" },
    ],
    [isArabic]
  )

  const values = Form.useWatch([], form)
  const allSelected =
    !!values?.roomType && !!values?.decoreStyle && !!values?.theme

  const handleSubmit = async () => {
    try {
      await form.validateFields()
      const { roomType, decoreStyle, theme, images } = form.getFieldsValue()
      const files = extractFiles(images)
      if (!files.length) {
        message.error(t.pleaseUpload)
        return
      }

      setUploading(true)
      const results = []

      for (const file of files) {
        validateImageFile(file, 1)
        const filename = generateUniqueFileName(file)
        const key = buildGalleryKey({ roomType, decoreStyle, theme, filename })
        await uploadFileToS3(file, key)
        results.push({
          key,
          url: getPublicUrl(key),
          roomType,
          decoreStyle,
          theme,
        })
      }

      message.success(`${t.successMsg} (${results.length})`)
      onAdd?.(results)
      form.resetFields()
      onCancel?.()
    } catch (err) {
      if (!err?.errorFields) {
        console.error(err)
        message.error(err?.message || "Upload failed")
      }
    } finally {
      setUploading(false)
    }
  }

  return (
    <Modal
      title={t.uploadImages}
      open={visible}
      onCancel={() => !uploading && onCancel?.()}
      footer={null}
      centered
      maskClosable={!uploading}
      className={isArabic ? "rtl-modal" : ""}
    >
      <Form
        form={form}
        layout="vertical"
        style={{ direction: isArabic ? "rtl" : "ltr" }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="roomType"
              rules={[{ required: true, message: t.requiredRoomType }]}
            >
              <Select placeholder={t.roomType} options={roomTypeOptions} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="decoreStyle"
              rules={[{ required: true, message: t.requiredDecorStyle }]}
            >
              <Select placeholder={t.decorStyle} options={decoreStyleOptions} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          name="theme"
          rules={[{ required: true, message: t.requiredTheme }]}
        >
          <Select placeholder={t.theme} options={themeOptions} />
        </Form.Item>
        <Form.Item
          name="images"
          rules={[
            {
              validator: (_, value) => {
                if (!allSelected) {
                  return Promise.reject(t.mustSelectAll)
                }
                const files = extractFiles(value)
                if (!files.length) {
                  return Promise.reject(t.pleaseUpload)
                }
                const tooBig = files.find((f) => f.size > 1 * 1024 * 1024)
                if (tooBig) {
                  return Promise.reject(`"${tooBig.name}" ${t.exceedsLimit}`)
                }
                return Promise.resolve()
              },
            },
          ]}
        >
          <CustomUpload
            acceptedFormats={[".png", ".jpeg", ".jpg"]}
            maxSizeMB={1}
            uploadText={t.uploadText}
            form={form}
            name="images"
            bulkUpload={true}
            onChange={(files) => form.setFieldsValue({ images: files })}
          />
        </Form.Item>
        <Form.Item>
          <Button
            className="add-store"
            type="primary"
            block
            onClick={handleSubmit}
            loading={uploading}
          >
            {uploading ? t.uploading : t.uploadBtn}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default UploadImagesModal
